<template>
  <woot-modal
    :show="show"
    :on-close="onClose"
    size="small"
    :show-close-button="false"
  >
    <div class="hub-modal">
      <!-- Header -->
      <div class="fb-header">
        <div class="hd-left">
          <button
            class="icon-btn"
            type="button"
            :aria-label="$t('HUB.FB_PAGE.CANCEL')"
            @click="onClose"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <h2>
          {{ step === 1 ? $t('HUB.FB_PAGE.TITLE') : $t('HUB.FB_PAGE.PREVIEW') }}
        </h2>
        <div class="hd-right" />
      </div>

      <!-- STEP 1: compose -->
      <div v-show="step === 1" class="step">
        <div class="compose-scroll">
          <!-- Posting-as page -->
          <div class="acct-row">
            <thumbnail :src="pagePicture" :username="pageName" size="40px" />
            <div class="acct-meta">
              <div class="acct-label">{{ $t('HUB.FB_PAGE.POSTING_AS') }}</div>
              <div class="acct-name">{{ pageName }}</div>
            </div>
          </div>

          <!-- Media: empty drop zone -->
          <button
            v-if="!images.length"
            class="empty-drop"
            :class="{ drag: isDragOver }"
            type="button"
            @click="triggerFileSelect"
            @dragover.prevent="isDragOver = true"
            @dragleave="isDragOver = false"
            @drop.prevent="onDrop"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 3v14" />
              <path d="M5 10l7-7 7 7" />
              <rect x="3" y="17" width="18" height="4" rx="1" />
            </svg>
            <div class="ed-title">{{ $t('HUB.FB_PAGE.ADD_PHOTOS_TITLE') }}</div>
            <div class="ed-sub">
              {{ $t('HUB.FB_PAGE.ADD_PHOTOS_SUBTITLE') }}
            </div>
            <span class="ed-btn">{{
              $t('HUB.FB_PAGE.SELECT_FROM_DEVICE')
            }}</span>
          </button>

          <!-- Media: carousel -->
          <div v-else class="carousel">
            <div
              v-for="(img, i) in images"
              :key="img.id"
              class="carousel-slide"
              :class="{ active: i === carIndex }"
            >
              <video v-if="img.type === 'video'" :src="img.url" controls />
              <img v-else :src="img.url" :alt="`Image ${i + 1}`" />
            </div>
            <button
              v-show="images.length > 1 && carIndex > 0"
              class="car-arrow prev"
              type="button"
              :aria-label="$t('HUB.FB_PAGE.BACK')"
              @click="carGo(-1)"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              v-show="images.length > 1 && carIndex < images.length - 1"
              class="car-arrow next"
              type="button"
              :aria-label="$t('HUB.FB_PAGE.NEXT')"
              @click="carGo(1)"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <div v-if="images.length > 1" class="car-count">
              {{ carIndex + 1 }}/{{ images.length }}
            </div>
            <div v-if="images.length > 1" class="car-dots">
              <span
                v-for="(img, i) in images"
                :key="`car-dot-${img.id}`"
                :class="{ active: i === carIndex }"
              />
            </div>
          </div>

          <!-- Media: thumbnail strip (click to view, drag to reorder) -->
          <div v-if="images.length" class="thumb-strip">
            <div
              v-for="(img, i) in images"
              :key="img.id"
              class="thumb"
              :class="{ active: i === carIndex, dragging: dragIndex === i }"
              draggable="true"
              @click="carSet(i)"
              @dragstart="onThumbDragStart(i)"
              @dragend="dragIndex = null"
              @dragover.prevent
              @drop="onThumbDrop(i)"
            >
              <video
                v-if="img.type === 'video'"
                :src="img.url"
                muted
                draggable="false"
              />
              <img v-else :src="img.url" alt="" draggable="false" />
              <button
                class="thumb-remove"
                type="button"
                :aria-label="$t('HUB.FB_PAGE.REMOVE_IMAGE')"
                @click.stop="removeImage(i)"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <button
              v-if="canAddMore"
              class="thumb-add"
              type="button"
              :aria-label="$t('HUB.FB_PAGE.ADD_MORE')"
              @click="triggerFileSelect"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept=".jpg,.jpeg,.png,.mp4"
            multiple
            hidden
            @change="onFileChange"
          />

          <!-- Message -->
          <div class="row">
            <div class="row-head">
              <label for="hub-fb-message">{{
                $t('HUB.FB_PAGE.MESSAGE_LABEL')
              }}</label>
              <span class="count">{{ messageCount }}</span>
            </div>
            <textarea
              id="hub-fb-message"
              v-model="message"
              class="msg-input"
              :maxlength="maxMessageLength"
              :placeholder="$t('HUB.FB_PAGE.MESSAGE_PLACEHOLDER')"
            />
          </div>

          <!-- Schedule (optional) -->
          <div class="row no-border">
            <div class="row-head">
              <label>
                {{ $t('HUB.FB_PAGE.SCHEDULE_LABEL') }}
                <span class="opt">({{ $t('HUB.FB_PAGE.OPTIONAL') }})</span>
              </label>
            </div>
            <div class="hub-schedule-picker">
              <date-picker
                type="datetime"
                :confirm="true"
                :clearable="true"
                :editable="false"
                format="DD/MM/YYYY HH:mm"
                :show-second="false"
                :confirm-text="$t('HUB.FB_PAGE.SCHEDULE_CONFIRM')"
                :placeholder="$t('HUB.FB_PAGE.SCHEDULE_PLACEHOLDER')"
                :value="scheduleAt"
                :disabled-date="isScheduleDayDisabled"
                @change="scheduleAt = $event"
              >
                <template #icon-clear>
                  <button type="button" class="hub-schedule-clear-btn">
                    {{ $t('HUB.FB_PAGE.CLEAR') }}
                  </button>
                </template>
              </date-picker>
            </div>
            <p
              v-if="isScheduled && !isScheduleValid"
              class="field-hint field-hint--error"
            >
              {{ $t('HUB.FB_PAGE.SCHEDULE_WINDOW_ERROR') }}
            </p>
            <p v-else class="field-hint">
              {{ $t('HUB.FB_PAGE.SCHEDULE_HINT') }}
            </p>
          </div>
        </div>
      </div>

      <!-- STEP 2: preview -->
      <div v-show="step === 2" class="step">
        <hub-facebook-post-preview
          :page-name="pageName"
          :page-picture="pagePicture"
          :message="message"
          :images="images"
          :scheduled="isScheduled"
          :scheduled-text="scheduleBannerText"
        />
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button
          v-show="step === 2"
          class="btn btn-ghost"
          type="button"
          @click="goToStep(1)"
        >
          {{ $t('HUB.FB_PAGE.BACK') }}
        </button>
        <button
          v-show="step === 1"
          class="btn btn-ghost"
          type="button"
          @click="onClose"
        >
          {{ $t('HUB.FB_PAGE.CANCEL') }}
        </button>
        <button
          v-show="step === 1"
          class="btn btn-primary"
          type="button"
          :disabled="!canProceed"
          @click="goToStep(2)"
        >
          {{ $t('HUB.FB_PAGE.NEXT') }}
        </button>
        <button
          v-show="step === 2"
          class="btn btn-primary"
          type="button"
          :disabled="isSubmitting"
          @click="publish"
        >
          {{ $t('HUB.FB_PAGE.SHARE') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import addDays from 'date-fns/addDays';
import DatePicker from 'vue2-datepicker';
import alertMixin from 'shared/mixins/alertMixin';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import HubFacebookPostPreview from './HubFacebookPostPreview.vue';
import hubClient from 'dashboard/api/channel/hubClient';

const MAX_IMAGES = 10;
// Facebook post message hard limit.
const MAX_MESSAGE_LENGTH = 63206;
// Upload restriction — mirrored on the backend (see Hub::PostsController).
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'mp4'];
const VIDEO_EXTENSIONS = ['mp4'];
// The hub's allowed scheduleTime window — mirrored on the backend
// (see Hub::PostsController::MIN_SCHEDULE_LEAD / MAX_SCHEDULE_LEAD).
const MIN_SCHEDULE_LEAD_MS = 11 * 60 * 1000;
const MAX_SCHEDULE_LEAD_MS = 30 * 24 * 60 * 60 * 1000;

export default {
  components: {
    Thumbnail,
    DatePicker,
    HubFacebookPostPreview,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    inbox: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      step: 1,
      message: '',
      images: [],
      carIndex: 0,
      scheduleAt: null,
      isDragOver: false,
      dragIndex: null,
      isSubmitting: false,
      uid: 0,
      maxImages: MAX_IMAGES,
      maxMessageLength: MAX_MESSAGE_LENGTH,
    };
  },
  computed: {
    pageAttributes() {
      return this.inbox?.additional_attributes || {};
    },
    pageName() {
      return this.pageAttributes.page_name || '';
    },
    pagePicture() {
      return this.pageAttributes.page_picture || '';
    },
    hasText() {
      return this.message.trim().length > 0;
    },
    canProceed() {
      return (this.images.length > 0 || this.hasText) && this.isScheduleValid;
    },
    hasVideo() {
      return this.images.some(item => item.type === 'video');
    },
    // A post is either up to 10 photos, or a single video (never both).
    canAddMore() {
      return !this.hasVideo && this.images.length < MAX_IMAGES;
    },
    isScheduled() {
      return Boolean(this.scheduleAt);
    },
    // The hub requires scheduleTime to be between 11 minutes and 30 days from
    // now, as an exact duration. No schedule at all (post immediately) is
    // always valid.
    isScheduleValid() {
      if (!this.isScheduled) return true;
      const delta = this.scheduleAt.getTime() - Date.now();
      return delta >= MIN_SCHEDULE_LEAD_MS && delta <= MAX_SCHEDULE_LEAD_MS;
    },
    scheduledPublishTime() {
      if (!this.isScheduled) return null;
      return Math.floor(this.scheduleAt.getTime() / 1000);
    },
    messageCount() {
      return this.message.length.toLocaleString();
    },
    scheduleBannerText() {
      if (!this.isScheduled) return '';
      const formatted = this.scheduleAt.toLocaleString(this.$i18n.locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
      return `${this.$t('HUB.FB_PAGE.SCHEDULED_FOR')} ${formatted}`;
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) this.reset();
    },
  },
  methods: {
    reset() {
      this.step = 1;
      this.message = '';
      this.images = [];
      this.carIndex = 0;
      this.scheduleAt = null;
      this.isDragOver = false;
      this.dragIndex = null;
      this.isSubmitting = false;
    },
    onClose() {
      this.$emit('update:show', false);
    },

    triggerFileSelect() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      this.addFiles(e.target.files);
      e.target.value = '';
    },
    onDrop(e) {
      this.isDragOver = false;
      if (e.dataTransfer?.files) this.addFiles(e.dataTransfer.files);
    },
    fileExtension(file) {
      return file.name.split('.').pop().toLowerCase();
    },
    isAllowedFile(file) {
      return ALLOWED_EXTENSIONS.includes(this.fileExtension(file));
    },
    isVideoFile(file) {
      return VIDEO_EXTENSIONS.includes(this.fileExtension(file));
    },
    addFiles(fileList) {
      const incoming = Array.from(fileList);
      if (!incoming.length) return;

      const allowed = incoming.filter(f => this.isAllowedFile(f));
      if (allowed.length < incoming.length) {
        this.showAlert(this.$t('HUB.FB_PAGE.INVALID_FILE_TYPE'));
      }
      if (!allowed.length) return;

      const videos = allowed.filter(f => this.isVideoFile(f));
      const photos = allowed.filter(f => !this.isVideoFile(f));

      // A post is either a single video or up to 10 photos — never both.
      if (videos.length) {
        if (videos.length > 1 || photos.length || this.images.length) {
          this.showAlert(this.$t('HUB.FB_PAGE.MEDIA_TYPE_CONFLICT'));
          return;
        }
        this.readFiles(videos);
        return;
      }
      if (this.hasVideo) {
        this.showAlert(this.$t('HUB.FB_PAGE.MEDIA_TYPE_CONFLICT'));
        return;
      }
      const room = MAX_IMAGES - this.images.length;
      if (room <= 0) {
        this.showAlert(this.$t('HUB.FB_PAGE.MAX_PHOTOS'));
        return;
      }
      this.readFiles(photos.slice(0, room));
    },
    readFiles(files) {
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = ev => {
          this.uid += 1;
          this.images.push({
            id: this.uid,
            url: ev.target.result,
            file,
            type: this.isVideoFile(file) ? 'video' : 'image',
          });
          this.carIndex = this.images.length - 1;
        };
        reader.readAsDataURL(file);
      });
    },
    removeImage(i) {
      this.images.splice(i, 1);
      if (this.carIndex >= this.images.length) {
        this.carIndex = Math.max(0, this.images.length - 1);
      }
    },

    carGo(delta) {
      this.carSet(this.carIndex + delta);
    },
    carSet(i) {
      this.carIndex = Math.max(0, Math.min(this.images.length - 1, i));
    },

    onThumbDragStart(i) {
      this.dragIndex = i;
    },
    onThumbDrop(target) {
      const from = this.dragIndex;
      this.dragIndex = null;
      if (from === null || from === target) return;
      const viewed = this.images[this.carIndex];
      const [moved] = this.images.splice(from, 1);
      this.images.splice(target, 0, moved);
      this.carIndex = Math.max(0, this.images.indexOf(viewed));
    },

    goToStep(n) {
      this.step = n;
    },

    // Grays out whole days outside the hub's allowed window; exact
    // minute-level enforcement is done by isScheduleValid.
    isScheduleDayDisabled(date) {
      const yesterday = addDays(new Date(), -1);
      const maxDay = new Date(Date.now() + MAX_SCHEDULE_LEAD_MS);
      return date < yesterday || date > maxDay;
    },

    async publish() {
      if (this.isSubmitting) return;
      if (!this.hasText && !this.images.length) return;
      // Re-check even though canProceed already gated "Next": the schedule
      // window is time-relative, so a valid pick on step 1 can go stale while
      // the user is still reviewing the preview on step 2.
      if (this.isScheduled && !this.isScheduleValid) {
        this.showAlert(this.$t('HUB.FB_PAGE.SCHEDULE_WINDOW_ERROR'));
        return;
      }
      this.isSubmitting = true;

      // The hub publishes immediately when scheduleTime is omitted, so we only
      // send scheduled_publish_time when the user picked a time.
      const payload = new FormData();
      payload.append('inbox_id', this.inbox.id);
      payload.append('message', this.message);
      if (this.scheduledPublishTime !== null) {
        payload.append('scheduled_publish_time', this.scheduledPublishTime);
      }
      this.images.forEach(img => payload.append('images[]', img.file));

      try {
        // Async: the backend enqueues the hub call and returns 202 Accepted.
        // The hub's own success/failure is logged server-side (see the job).
        await hubClient.createPost(payload);
        this.showAlert(this.$t('HUB.FB_PAGE.CREATE_SUCCESS'));
        this.onClose();
      } catch (error) {
        // Don't fail silently — log the real error we got back.
        // eslint-disable-next-line no-console
        console.error(
          'Hub create Facebook post failed:',
          error?.response?.data || error?.message || error
        );
        this.showAlert(this.$t('HUB.FB_PAGE.CREATE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* The woot-modal shell owns the backdrop, size (medium) and rounded/shadow
   container. This inner surface stays intentionally light (Facebook composer)
   regardless of the app theme, filling the container edge to edge. */
.hub-modal {
  --cw-green: #23a06d;
  --cw-green-dark: #1f845a;
  --cw-green-tint: #eaf6f0;
  --cw-green-border: #b7ddca;
  --btn-disabled: #e5e7eb;
  --error-red: #b91c1c;
  --fb-blue: #1877f2;
  --fb-surface: #f0f2f5;
  --ink: #262626;
  /* login palette: gray-700 text, gray-500 muted, gray-300 borders */
  --ink-soft: #374151;
  --muted: #6b7280;
  --line: #d1d5db;
  --line-soft: #efefef;
  --surface: #ffffff;
  --radius-sm: 5px;
  --radius-input: 8px;
  --shadow-xs: 0px 1px 2px rgba(17, 24, 39, 0.1);
  --focus: 0 0 0 3px rgba(31, 132, 90, 0.18);

  width: 100%;
  max-height: calc(100vh - 4rem);
  background: var(--surface);
  color: var(--ink-soft);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.hub-modal *,
.hub-modal *::before,
.hub-modal *::after {
  box-sizing: border-box;
}

/* Header */
.fb-header {
  direction: ltr;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 46px;
  padding: 0 8px 0 12px;
  border-bottom: 1px solid var(--line);
  flex: none;
}
.fb-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
  text-align: center;
  flex: 1;
}
.hd-left,
.hd-right {
  display: flex;
  align-items: center;
  min-width: 40px;
}
.hd-right {
  justify-content: flex-end;
}
.icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: var(--ink);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s;
}
.icon-btn:hover {
  background: var(--line-soft);
}
.icon-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus);
}

/* Steps */
.step {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.compose-scroll {
  flex: 1;
  overflow-y: auto;
}

/* Posting-as row */
.acct-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line-soft);
}
.acct-meta {
  min-width: 0;
  flex: 1;
}
.acct-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.acct-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  margin-top: 1px;
}

/* Media: empty drop */
.empty-drop {
  width: 100%;
  border: none;
  background: #fafafa;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  color: var(--ink);
  border-bottom: 1px solid var(--line-soft);
}
.empty-drop.drag {
  background: var(--cw-green-tint);
}
.empty-drop svg {
  width: 46px;
  height: 46px;
  stroke-width: 1;
}
.empty-drop .ed-title {
  font-size: 15px;
  margin-top: 10px;
}
.empty-drop .ed-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}
.empty-drop .ed-btn {
  margin-top: 16px;
  background: var(--cw-green);
  color: #fff;
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 600;
  padding: 9px 16px;
  transition: background-color 0.2s ease;
}
.empty-drop:hover .ed-btn {
  background-color: var(--cw-green-dark);
}
.empty-drop:focus-visible {
  outline: none;
  box-shadow: inset var(--focus);
}

/* Media: carousel */
.carousel {
  position: relative;
  width: 100%;
  height: 260px;
  background: #000;
  border-bottom: 1px solid var(--line-soft);
}
.carousel-slide {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s;
}
.carousel-slide.active {
  opacity: 1;
}
.carousel-slide img,
.carousel-slide video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.car-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: #262626;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  z-index: 2;
}
.car-arrow:hover {
  background: #fff;
}
.car-arrow.prev {
  left: 12px;
}
.car-arrow.next {
  right: 12px;
}
.car-arrow svg {
  width: 16px;
  height: 16px;
}
.car-dots {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  display: flex;
  gap: 6px;
  justify-content: center;
}
.car-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
}
.car-dots span.active {
  background: #fff;
}
.car-count {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
}

/* Media: thumbnail strip */
.thumb-strip {
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid var(--line-soft);
  overflow-x: auto;
}
.thumb {
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 6px;
  overflow: hidden;
  flex: none;
  cursor: grab;
  border: 2px solid transparent;
  user-select: none;
}
.thumb.active {
  border-color: var(--cw-green);
}
.thumb.dragging {
  opacity: 0.55;
  cursor: grabbing;
}
.thumb img,
.thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.thumb-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.thumb-remove svg {
  width: 10px;
  height: 10px;
}
.thumb-add {
  width: 54px;
  height: 54px;
  border-radius: 6px;
  flex: none;
  border: 1.5px dashed var(--line);
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: var(--muted);
}
.thumb-add:hover {
  border-color: var(--cw-green);
  color: var(--cw-green-dark);
}
.thumb-add svg {
  width: 20px;
  height: 20px;
}

/* Fields */
.row {
  display: block;
  padding: 14px 16px;
  border-bottom: 1px solid var(--line-soft);
}
.row.no-border {
  border-bottom: none;
}
.row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.row label {
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: var(--ink-soft);
}
.row label .opt {
  color: var(--muted);
  font-weight: 500;
}
.count {
  font-size: 12px;
  color: var(--muted);
}

/* Inputs: match the login form field style (see v3/DefaultLogin/Input.vue). */
.msg-input,
.control {
  width: 100%;
  margin-top: 8px;
  font-family: inherit;
  font-size: 16px;
  color: var(--ink-soft);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-input);
  box-shadow: var(--shadow-xs);
  padding: 10px 14px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.msg-input::placeholder,
.control::placeholder {
  color: var(--muted);
}
.msg-input:focus,
.control:focus {
  outline: none;
  border-color: var(--cw-green);
  box-shadow: var(--focus);
}
.msg-input {
  resize: none;
  line-height: 1.5;
  min-height: 120px;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--muted);
}
.field-hint--error {
  color: var(--error-red);
}

/* Schedule field: style the shared date-time picker to match .control, and
   show its clear button as always-visible text next to the calendar icon
   (instead of the library's default hover-only "x"). Scoped to this modal
   only — other date pickers in the app are unaffected. */
.hub-schedule-picker {
  margin-top: 8px;
}
.hub-schedule-picker ::v-deep .mx-datepicker {
  width: 100%;
}
.hub-schedule-picker ::v-deep .mx-input {
  height: 44px;
  font-family: inherit;
  font-size: 16px;
  color: var(--ink-soft);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-input);
  box-shadow: var(--shadow-xs);
  padding: 0 64px 0 14px;
}
.hub-schedule-picker ::v-deep .mx-input:hover {
  border-color: #c4c4c4;
}
.hub-schedule-picker ::v-deep .mx-input:focus {
  outline: none;
  border-color: var(--cw-green);
  box-shadow: var(--focus);
}
.hub-schedule-picker ::v-deep .mx-icon-calendar {
  color: var(--muted);
}
.hub-schedule-clear-btn {
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: var(--cw-green-dark);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;
}
.hub-schedule-clear-btn:hover {
  text-decoration: underline;
}
/* Always show the clear button (the library hides it until hover by
   default) and keep the calendar icon visible alongside it. */
.hub-schedule-picker ::v-deep .mx-icon-clear {
  display: inline-flex !important;
  align-items: center;
  right: 34px;
}
.hub-schedule-picker
  ::v-deep
  .mx-input-wrapper:hover
  .mx-icon-clear
  + .mx-icon-calendar {
  display: inline-flex !important;
}
[dir='rtl'] .hub-schedule-picker ::v-deep .mx-input {
  padding: 0 14px 0 64px;
}
[dir='rtl'] .hub-schedule-picker ::v-deep .mx-icon-clear {
  right: unset;
  left: 34px;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid var(--line);
  flex: none;
  background: #fff;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  border-radius: var(--radius-sm);
  padding: 0 22px;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
}
.btn-ghost {
  background: #fff;
  color: var(--ink-soft);
  border: 1px solid var(--line);
}
.btn-ghost:hover {
  background: var(--line-soft);
}
.btn-primary {
  background: var(--cw-green);
  color: #fff;
  border: 1px solid var(--cw-green);
}
.btn-primary:not(:disabled):hover {
  background-color: var(--cw-green-dark);
  border-color: var(--cw-green-dark);
}
.btn-primary:disabled {
  background-color: var(--btn-disabled);
  border-color: var(--btn-disabled);
  color: #9ca3af;
  cursor: not-allowed;
}
.btn:focus-visible {
  outline: none;
  box-shadow: var(--focus);
}
</style>
